import React, { Dispatch, RefAttributes, memo } from "react";
import { Row, react, useRefWithSetter } from "uikit";
import { FieldErrors } from "react-hook-form";

import useObjectEditor from "../../../../../../../../../hooks/useObjectEditor";
import PaymentSystem, {
	PaymentProvider,
} from "../../../../../../../../../services/PaymentSystem";
import {
	StyledRow,
	StyledColumn,
} from "../../../../../../../../../components/common";
import {
	CompanyAndTaxiService,
	EasyPayPayment,
	OtvPayment,
	Name,
	RefundPolicy,
	Root,
	SelectPaymentSystems,
} from "../../../../../components";

import InternalController from "./Controller";
import { TERMINAL_PROVIDERS } from "../../../../../constants/providers";

const ContentBase = react.withController<Content.PropsBase, Content.Controller>(
	({ value, onChange, controller }) => {
		const [nameRef, setNameRef] = useRefWithSetter<Name.Ref | null>(null);
		const [refundPolicyRef, setRefundPolicyRef] =
			useRefWithSetter<RefundPolicy.Ref | null>(null);
		const [companyBranchRef, setCompanyBranchRef] =
			useRefWithSetter<CompanyAndTaxiService.Ref | null>(null);
		const [paymentSystemRef, setPaymentSystemRef] =
			useRefWithSetter<SelectPaymentSystems.Ref | null>(null);
		const [easyPayPaymentRef, setEasyPayPaymentRef] =
			useRefWithSetter<EasyPayPayment.Ref | null>(null);
		const [otvPaymentRef, setOtvPaymentRef] =
			useRefWithSetter<OtvPayment.Ref | null>(null);

		const valueEditor = useObjectEditor(value, onChange);

		const isDefault = valueEditor.get("isDefault");

		const name = valueEditor.useGetter("name");
		const setName = valueEditor.useSetter("name");

		const refundPolicyUrl = valueEditor.useGetter("refundPolicyUrl");
		const setRefundPolicyUrl = valueEditor.useSetter("refundPolicyUrl");

		const paymentProviderValue = valueEditor.useGetter("paymentProvider");

		const taxiServicesData = valueEditor.usePicker(
			["taxiServiceIds", "taxiServices", "companies", "companyIds"],
			"json",
		);

		const updateAllData = valueEditor.useAssigner();

		controller.setContext({
			nameRef,
			refundPolicyRef,
			companyBranchRef,
			paymentSystemRef,
			paymentProviderValue,
			easyPayPaymentRef,
			otvPaymentRef,
		});
		return (
			<Root hasPaddings>
				<StyledColumn>
					<StyledColumn gap="25px" w="100%" h="100%">
						<Row sizes="40% 30% 30%" gaps="16px*">
							<Name
								ref={setNameRef}
								name={name}
								setName={setName}
								isDefault={isDefault}
							/>
							<CompanyAndTaxiService
								ref={setCompanyBranchRef}
								value={taxiServicesData}
								onChange={updateAllData}
							/>
						</Row>
						<StyledRow justify="start" gap="16px">
							<SelectPaymentSystems
								ref={setPaymentSystemRef}
								value={value}
								onChange={onChange}
								providers={TERMINAL_PROVIDERS}
							/>
						</StyledRow>
						<StyledRow gap="20px" justify="space-between" w="100%">
							<StyledColumn gap="10px" w="100%">
								{paymentProviderValue ===
									PaymentProvider.EASYPAY && (
									<EasyPayPayment
										ref={setEasyPayPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}
								{paymentProviderValue ===
									PaymentProvider.OTV && (
									<OtvPayment
										ref={setOtvPaymentRef}
										value={value}
										onChange={onChange}
									/>
								)}
							</StyledColumn>
						</StyledRow>
						<RefundPolicy
							ref={setRefundPolicyRef}
							value={refundPolicyUrl}
							onChange={setRefundPolicyUrl}
						/>
					</StyledColumn>
				</StyledColumn>
			</Root>
		);
	},
	InternalController,
);
const Content = memo(ContentBase);

declare namespace Content {
	type Ref = InternalController | null;
	type Controller = InternalController;
	interface PropsBase {
		value: Value;
		onChange: Dispatch<Value>;
		errors?: FieldErrors<any>;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	interface Value extends PaymentSystem.Model {}
}

export default Content;
